import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTime as LuxonDateTime } from 'luxon';
import { Communication } from 'src/providers/communication/communication';
import { Cleanings } from 'src/providers/cleanings/cleanings';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Me } from 'src/shared/providers/me';
import { Timeout } from 'src/shared/components/timeout/timeout';
import { TimeoutErrorHandler } from 'src/shared/providers/http/timeout-error-handler';

import { Loading } from 'src/shared/components/loading/loading';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MWService } from 'src/main-workflow/mw.service';
import { CommonUtils } from 'src/shared/utils/common-utils';

@Component({
    templateUrl: 'past-job-messages.html',
    standalone: false
})
export class PastJobMessagesPage extends Timeout {

  errorMessage: string;
  job: any;
  messages: any[] = [];
  submitted: boolean;
  form: UntypedFormGroup;
  pageNumber = 1;
  totalMessages = 0;
  isLoadingMore = false;
  readonly messagesPerPage = 50;

  constructor(
    public communication: Communication,
    private cleanings: Cleanings,
    private me: Me,
    private navCtrl: CustomNavController,
    public route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private mwService: MWService,
    router: Router,
    timeoutErrorHandler: TimeoutErrorHandler,
    private utils: CommonUtils
  ) {
    super(timeoutErrorHandler, router, route);
    this.form = this.fb.group({
      message: ['', Validators.required],
    });
  }

  @Loading()
  async ionViewDidEnter() {
    try {
      this.loaded = false;
      const jobId = parseInt(this.route.snapshot.paramMap.get('jobId'));
      this.job = this.navCtrl.getParam('cleaning') || await this.cleanings.fetchCleaning(jobId);
      this.job.homekeeper_job_id = this.job.homekeeper_job.id;
      const result = await this.communication.getSharedInboxPaginated(this.job.customer.id, null, 1, this.messagesPerPage);
      this.messages = result.messages;
      this.totalMessages = result.totalRecords;
      this.pageNumber = 1;
      await this.markMessagesAsRead();
      this.loaded = true;
    } catch (err) {
      this.timeoutHandler(err);
      this.errorMessage = err.error ? err.error.message : err.message;
      this.utils.showError(this.errorMessage);
    }
  }

  async markMessagesAsRead() {
    let array: any = [];
    this.messages.map((message) => {
      if (!message.is_read && message.from_member.type == 'CustomerMember' && message?.chat_room_key) {
        array.push((message?.chat_room_key));
      }
    });
    if (array.length > 0) {
      await this.communication.markMessagesAsRead(array);
    }
  }

  hasPassed14DaysFromJobStartDate() {
    const jobDate = LuxonDateTime.fromISO(this.job.job.date);
    const now = LuxonDateTime.now();
    const diff = now.diff(jobDate, ['days']);
    return diff.days > 14;
  }

  contactClient(type) {
    if (type == 'attachment') {
      this.attachment();
      return;
    }
    if (type == 'message') {
      this.message();
      return;
    }
    this.call();
  }

  @Loading()
  async message(attachment = []) {
    this.errorMessage = '';
    this.submitted = true;
    if (attachment.length == 0 && !this.form.get('message').valid) {
      return;
    }
    const payload = {
      to_customer_id: this.job.customer.id,
      message: {
        text: this.form.value.message,
        files: attachment
      }
    };
    try {
      await this.mwService.startCommunication(this.job, false);
      const response = await this.communication.sendSharedInboxMessage(payload);
      if (response?.data?.message == 'We couldn\'t send your message, please try again later!') {
        return this.errorMessage = response.data.message;
      }
      // Clear the submitted flag before emptying the field so the composer
      // never renders an error/red state while we reset it after a successful send.
      this.submitted = false;
      this.form.patchValue({
        message: ''
      });
      const msgResult = await this.communication.getSharedInboxPaginated(this.job.customer.id, null, 1, this.messagesPerPage);
      this.messages = msgResult.messages;
      this.totalMessages = msgResult.totalRecords;
      this.pageNumber = 1;
    } catch (err) {
      const error = await this.mwService.buildErrorAlert(err);
      return this.showErrorMessage(error);
    }
  }

  async attachment() {
    this.errorMessage = '';
    try {
      const attachment = await this.communication.addAttachment();
      if (attachment == '') {
        return this.errorMessage = 'Unable to attach photo. Please upload a PNG or JPEG file.';
      }
      await this.message([attachment]);
    } catch (err) {
      this.errorMessage =  err.error ? err.error.message : err.message;
      this.utils.showError(this.errorMessage);
    }
  }

  async call() {
    try {
      await this.mwService.startCommunication(this.job, false);
    } catch (err) {
      const error = await this.mwService.buildErrorAlert(err);
      return this.showErrorMessage(error);
    }
    if (!this.job.gateway_phone) {
      return this.utils.showInfo('Calling isn\'t available for this client. Send a message instead.');
    }
    window.location.href = `tel:${this.job.gateway_phone}`;
  }

  get hasMoreMessages(): boolean {
    return this.messages?.length < this.totalMessages;
  }

  async loadMoreMessages() {
    if (this.isLoadingMore || !this.hasMoreMessages) return;
    this.isLoadingMore = true;
    this.pageNumber++;
    try {
      const result = await this.communication.getSharedInboxPaginated(this.job.customer.id, null, this.pageNumber, this.messagesPerPage);
      this.messages = [...this.messages, ...result.messages];
    } catch {
      this.pageNumber--;
    } finally {
      this.isLoadingMore = false;
    }
  }

  trackByMessageId(index: number, message: any): any {
    return message.id || message.chat_room_key || index;
  }

  showErrorMessage(message) {
    this.errorMessage = message;
    this.utils.showError(this.errorMessage);
    setTimeout(() => this.errorMessage = null, 10000);
  }
}
